import React from 'react';
import { useLocation } from 'react-router-dom';

import { useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { useSelector } from 'react-redux';

import { Page, LayoutSingleColumn, NamedLink } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './FromOldminePage.module.css';

/**
 * Landing page for visitors arriving from Oldmine's buttons on sold pieces
 * ("Looking for one like it" / "Search for it on Nextmine ISO" / the
 * "I bought this" seal). Instead of dropping them straight onto the login
 * screen, this page explains what Nextmine is and what happens next, then
 * forwards every prefill query param to the real destination.
 *
 * Query params:
 *   dest=iso (default)  -> continue to NewListingPage (/l/new) with params
 *   dest=collection     -> continue to MyCollectionPage (/my-collection)
 *   ...all other params (title, description, listingType, image, ...) are
 *   passed through untouched.
 */
const FromOldminePage = () => {
  const intl = useIntl();
  const scrollingDisabled = useSelector(isScrollingDisabled);
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const isCollection = params.get('dest') === 'collection';
  const pieceTitle = params.get('title');
  // "Add all my pieces": ?batch=handle1,handle2,... (flere stykker paa en gang)
  const batchCount = (params.get('batch') || '').split(',').filter(Boolean).length;
  const isBatch = isCollection && batchCount > 1;
  params.delete('dest');
  const forwardedSearch = params.toString() ? `?${params.toString()}` : '';

  const title = intl.formatMessage({ id: 'FromOldminePage.title' });

  const continueLink = isCollection ? (
    <NamedLink name="MyCollectionPage" to={{ search: forwardedSearch }} className={css.cta}>
      {isBatch ? 'Continue - add them to my collection' : 'Continue - add it to my collection'}
    </NamedLink>
  ) : (
    <NamedLink name="NewListingPage" to={{ search: forwardedSearch }} className={css.cta}>
      Continue - post my search notice
    </NamedLink>
  );

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <p className={css.eyebrow}>Welcome from Oldmine</p>
          <h1 className={css.title}>
            {isBatch
              ? 'These pieces are yours'
              : isCollection
              ? 'This piece is yours'
              : 'Sold, but not gone'}
          </h1>
          <p className={css.meta}>
            You've arrived at Nextmine - Oldmine's circle of collectors, where antique and vintage
            jewelry finds its next home.
          </p>

          {isBatch ? (
            <div className={css.piece}>
              <p className={css.pieceLabel}>Your pieces</p>
              <p className={css.pieceTitle}>{batchCount} pieces you found at Oldmine</p>
            </div>
          ) : null}

          {pieceTitle ? (
            <div className={css.piece}>
              <p className={css.pieceLabel}>{isCollection ? 'Your piece' : 'You were admiring'}</p>
              <p className={css.pieceTitle}>{pieceTitle}</p>
            </div>
          ) : null}

          {isCollection ? (
            <>
              <p className={css.p}>
                {isBatch
                  ? 'You bought these pieces at Oldmine - now give them a shelf of their own.'
                  : 'You bought this piece at Oldmine - now give it a shelf of its own.'}{' '}
                My Collection on Nextmine is a quiet place to keep your pieces: their photos, their
                stories, and everything you know about them, gathered in one spot. Share your
                collection with friends if you like, or keep it entirely to yourself. And should you
                ever wish to pass a piece on, your collection is the natural place to start.
              </p>
              <div className={css.steps}>
                <p className={css.stepsTitle}>What happens next</p>
                <ol>
                  <li>
                    Create a free account, or sign in if you have one. It takes about a minute.
                  </li>
                  <li>
                    {isBatch
                      ? "Your pieces are listed for you - untick any you no longer own. Oldmine's photos come along too."
                      : "Your piece is already filled in for you - Oldmine's photos come along too."}
                  </li>
                  <li>
                    {isBatch
                      ? 'Add them all in one click, and your collection has begun.'
                      : 'Save it, and your collection has begun.'}
                  </li>
                </ol>
              </div>
            </>
          ) : (
            <>
              <p className={css.p}>
                The piece you were admiring has found a new home - but on Nextmine, sold doesn't
                have to mean gone. Post a free search notice, and collectors who own this piece (or
                one just like it) can see that you're looking. Maybe the new owner is ready to pass
                it on. Maybe someone else has been keeping one in a drawer, waiting for the right
                person to ask.
              </p>
              <div className={css.steps}>
                <p className={css.stepsTitle}>What happens next</p>
                <ol>
                  <li>
                    Create a free account, or sign in if you have one. It takes about a minute.
                  </li>
                  <li>
                    Your search notice is already filled in for you - just look it over and post it.
                  </li>
                  <li>
                    It appears on our{' '}
                    <NamedLink name="WantedPage" className={css.link}>
                      Wanted
                    </NamedLink>{' '}
                    page, where sellers browse for pieces to offer. You'll hear from us the moment
                    someone responds.
                  </li>
                </ol>
              </div>
            </>
          )}

          <div className={css.ctaWrap}>
            {continueLink}
            <p className={css.ctaNote}>
              Free, and you can remove it again whenever you like. Every sale on Nextmine is
              protected -{' '}
              <NamedLink name="HowPaymentsWorkPage" className={css.link}>
                here's how payments work
              </NamedLink>
              .
            </p>
          </div>

          <p className={css.browse}>
            Not quite ready?{' '}
            <NamedLink name="SearchPage" className={css.link}>
              Have a look at what's for sale first
            </NamedLink>
            .
          </p>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default FromOldminePage;
